/**
 * ProfileEntrepriseMediationController — onglet « Médiation & CGV B2C »
 * de /profil-entreprise.html (EPIC-007 / 7.2 + 7.7).
 *
 * Pilote 2 nm-edit-block indépendants :
 *   - block-mediateur : nom + adresse + site + email du médiateur
 *     consommateur (§2.J — code de la consommation art. L612-1).
 *   - block-cgv-b2c   : texte CGV brut + flag « joindre auto en pages
 *     annexes des PDF B2C ».
 *
 * Hydratation initiale : `GET /organization/profile` est appelé
 * dans `connect()` et renvoie `{bank, mediator, cgv}` ; on hydrate
 * uniquement les blocs `mediator` + `cgv` ici (le bloc `bank` est
 * pris en charge par ProfileEntrepriseBankController).
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : tous les résumés sont rendus depuis
 *     <template> (`tpl-mediation-*`) clonés via cloneTemplate / fillSlots.
 *   - Aucune classe CSS inventée.
 *   - Vocabulaire UI : « médiateur consommateur », « CGV B2C » (maquette).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class ProfileEntrepriseMediationController extends BaseController {
  static targets = [
    "blockMediateur",
    "blockCgv",
    "mediateurSummary",
    "cgvSummary",
    "nameInput",
    "addressInput",
    "websiteInput",
    "emailInput",
    "cgvText",
    "attachToggle",
    "mediateurSubmit",
    "cgvSubmit",
  ];

  state = {
    mediator: { name: null, postalAddress: null, website: null, contactEmail: null },
    cgv: { text: null, attachToDocuments: true, updatedAt: null },
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  async connect() {
    try {
      const data = await api.getOrganizationProfile();
      this._hydrate(data);
    } catch (error) {
      console.warn("[profile-entreprise-mediation] hydration failed", error);
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._populateInputs(this.state);
    this._open(block);
  }

  cancelBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._populateInputs(this.state);
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Submit handlers
  // ---------------------------------------------------------------------------

  async submitMediator(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const payload = {
      name: this._readInput(this.nameInputTarget),
      postalAddress: this._readInput(this.addressInputTarget),
      website: this._readInput(this.websiteInputTarget),
      contactEmail: this._readInput(this.emailInputTarget),
    };
    if (payload.contactEmail && !this.isValidEmail(payload.contactEmail)) {
      this.showError("Email du médiateur invalide.");
      return;
    }
    if (payload.website && !this._isValidUrl(payload.website)) {
      this.showError("URL du site du médiateur invalide (http(s) requis).");
      return;
    }
    await this._submit(
      () => api.updateMediator(payload),
      this.hasMediateurSubmitTarget ? this.mediateurSubmitTarget : null,
      this.hasBlockMediateurTarget ? this.blockMediateurTarget : null,
      "Coordonnées du médiateur enregistrées.",
    );
  }

  async submitCgv(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const text = this.hasCgvTextTarget ? this.cgvTextTarget.value : "";
    const attachToDocuments = this.hasAttachToggleTarget
      ? !!this.attachToggleTarget.checked
      : true;
    const payload = {
      text: text === "" ? null : text,
      attachToDocuments,
    };
    await this._submit(
      () => api.updateCgv(payload),
      this.hasCgvSubmitTarget ? this.cgvSubmitTarget : null,
      this.hasBlockCgvTarget ? this.blockCgvTarget : null,
      "CGV B2C enregistrées.",
    );
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  _hydrate(data) {
    if (!data || typeof data !== "object") return;
    if (data.mediator) {
      this.state.mediator = {
        name: data.mediator.name ?? null,
        postalAddress: data.mediator.postalAddress ?? null,
        website: data.mediator.website ?? null,
        contactEmail: data.mediator.contactEmail ?? null,
      };
    }
    if (data.cgv) {
      this.state.cgv = {
        text: data.cgv.text ?? null,
        attachToDocuments: data.cgv.attachToDocuments !== false,
        updatedAt: data.cgv.updatedAt ?? null,
      };
    }
    this._renderMediateurSummary();
    this._renderCgvSummary();
    this._populateInputs(this.state);
  }

  _populateInputs(state) {
    if (this.hasNameInputTarget) this.nameInputTarget.value = state.mediator.name ?? "";
    if (this.hasAddressInputTarget) this.addressInputTarget.value = state.mediator.postalAddress ?? "";
    if (this.hasWebsiteInputTarget) this.websiteInputTarget.value = state.mediator.website ?? "";
    if (this.hasEmailInputTarget) this.emailInputTarget.value = state.mediator.contactEmail ?? "";
    if (this.hasCgvTextTarget) this.cgvTextTarget.value = state.cgv.text ?? "";
    if (this.hasAttachToggleTarget) this.attachToggleTarget.checked = state.cgv.attachToDocuments !== false;
  }

  _renderMediateurSummary() {
    if (!this.hasMediateurSummaryTarget) return;
    const m = this.state.mediator;
    const hasAny = m.name || m.postalAddress || m.website || m.contactEmail;
    const tplId = hasAny ? "tpl-mediation-mediateur-summary-filled" : "tpl-mediation-mediateur-summary-empty";
    const fragment = this.cloneTemplate(tplId);
    if (hasAny) {
      this.fillSlots(fragment, {
        name: m.name ?? "—",
        website: m.website ?? "—",
      });
      // Le lien <a> doit recevoir le href correctement
      const link = fragment.querySelector("a[data-slot-href='website-href']");
      if (link && m.website) {
        link.setAttribute("href", m.website);
      } else if (link) {
        link.removeAttribute("href");
      }
    }
    this._replaceChildren(this.mediateurSummaryTarget, fragment);
  }

  _renderCgvSummary() {
    if (!this.hasCgvSummaryTarget) return;
    const cgv = this.state.cgv;
    const hasText = !!cgv.text && cgv.text.trim().length > 0;
    const tplId = hasText ? "tpl-mediation-cgv-summary-filled" : "tpl-mediation-cgv-summary-empty";
    const fragment = this.cloneTemplate(tplId);
    if (hasText) {
      this.fillSlots(fragment, {
        attachLabel: cgv.attachToDocuments ? "activé" : "désactivé",
        updatedLabel: cgv.updatedAt
          ? `Dernière modification : ${this.formatShortDate(cgv.updatedAt)}`
          : "",
      });
    } else {
      this.fillSlots(fragment, {
        attachLabel: cgv.attachToDocuments ? "activé" : "désactivé",
      });
    }
    this._replaceChildren(this.cgvSummaryTarget, fragment);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readInput(input) {
    if (!input) return null;
    const v = (input.value ?? "").trim();
    return v === "" ? null : v;
  }

  _isValidUrl(url) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  }

  async _submit(apiCall, submitButton, block, successMessage) {
    if (submitButton) submitButton.disabled = true;
    try {
      const data = await apiCall();
      this._hydrate(data);
      if (block) this._close(block);
      this.showSuccess(successMessage);
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour.";
      this.showError(msg);
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
